import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Clock, Calendar, ExternalLink, Download, FileText, User, ArrowUpDown, ArrowUp, ArrowDown, MessageSquare, AlertCircle, Package, Trash2, RefreshCw } from 'lucide-react';
import { ThemeColors, BorderRadius, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { Lightbox } from '../../components/common/Lightbox';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { ReplyForm } from './ReplyForm';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface Media {
  url?: string;
  dataUrl?: string;
  type?: string;
  name?: string;
  filename?: string;
}

interface CompetitorDisplayData {
  id: string;
  name: string;
  photoUrls?: string[];
  stockLevel: number;
  displayPanels?: number;
}

type VisitType = 'setup' | 'maintenance' | 'requested' | 'other' | '';

interface Note {
  id: string;
  text: string;
  date: string;
  time?: string;
  author: string;
  salesRepName?: string;
  visitType?: VisitType;
  visitReason?: string;
  competitors?: string[];
  competitorData?: CompetitorDisplayData[];
  media?: Media[];
  documents?: Media[];
  nextVisitDate?: string;
  storeLevel?: number;
  lowStockCategories?: string[];
  requestManagerContact?: boolean;
  replies?: Note[];
}

interface NotesListProps {
  notes: Note[];
  onViewMediaFullScreen?: (media: Media) => void;
  onSubmitReply?: (noteId: string, text: string, media: File[], documents: File[], compressionEnabled?: boolean) => Promise<void>;
  onDeleteNote?: (noteId: string) => void;
  onDeleteReply?: (noteId: string, replyId: string) => void;
  onRefreshNotes?: () => Promise<void>;
  isManager?: boolean;
  currentUserEmail?: string;
}

type SortOption = 'newest' | 'oldest' | 'author' | 'author-desc';

const Container = styled.div`
  margin-top: ${Spacing.md};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.md};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${Spacing.md};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  margin: 0;
  color: ${ThemeColors.text.primary};
  font-family: ${Typography.fontFamily.secondary};
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const SortLabel = styled.label`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  white-space: nowrap;
`;

const SortSelect = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${Spacing.md} center;
  background-size: 16px;
  padding-right: ${Spacing.xl};
  cursor: pointer;
  min-width: 140px;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
`;

const SortIcon = styled.div<{ sortOption: SortOption }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${BorderRadius.md};
  background-color: ${ThemeColors.gray[100]};
  color: ${ThemeColors.text.secondary};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.gray[200]};
    color: ${ThemeColors.text.primary};
  }
  
  ${props => {
    switch (props.sortOption) {
      case 'newest':
        return `color: ${ThemeColors.primary.main};`;
      case 'oldest':
        return `color: ${ThemeColors.primary.main};`;
      case 'author':
        return `color: ${ThemeColors.primary.main};`;
      case 'author-desc':
        return `color: ${ThemeColors.primary.main};`;
      default:
        return '';
    }
  }}
`;

const RefreshNotesButton = styled.button<{ spinning?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 12px;
  background-color: ${ThemeColors.background.paper};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    animation: ${props => props.spinning ? 'spin 0.8s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  &:hover:not(:disabled) {
    background-color: ${ThemeColors.gray[100]};
    border-color: ${ThemeColors.primary.light};
    color: ${ThemeColors.primary.main};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RefreshHintBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.primary.light}18;
  border: 1px solid ${ThemeColors.primary.light}50;
  border-radius: ${BorderRadius.sm};
  margin-bottom: ${Spacing.sm};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  line-height: 1.4;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xl};
  background-color: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.lg};
  border: 1px solid ${ThemeColors.border.light};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
    border-radius: ${BorderRadius.md};
  }
`;

const EmptyStateText = styled.p`
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.md};
  text-align: center;
`;

const NoteCard = styled.div`
  background-color: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.card};
  overflow: hidden;
  margin-bottom: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.light};
  
  @media (max-width: 768px) {
    border-radius: ${BorderRadius.md};
    margin-bottom: ${Spacing.md};
  }
`;

const NoteHeader = styled.div`
  padding: ${Spacing.md} ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: ${ThemeColors.gray[50]};
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm} ${Spacing.md};
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Author = styled.div`
  font-size: ${Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const AuthorEmail = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-top: 2px;
`;

const TimeInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const DateText = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const TimeText = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const NoteBody = styled.div`
  padding: ${Spacing.lg};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md};
  }
`;

const NoteText = styled.p`
  font-size: ${Typography.fontSize.base};
  color: ${ThemeColors.text.primary};
  line-height: ${Typography.lineHeight.relaxed};
  margin-bottom: ${Spacing.md};
  white-space: pre-wrap;
`;

const NextVisitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.primary.light}33;
  color: ${ThemeColors.primary.dark};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  margin-bottom: ${Spacing.md};
`;

const Section = styled.div`
  margin-top: ${Spacing.md};
`;

const SectionHeader = styled.h4`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const CompetitorList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
`;

const CompetitorBadge = styled.span`
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.primary.light}33;
  color: ${ThemeColors.primary.dark};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
`;

const LowStockBadge = styled.span`
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.warning.light}33;
  color: ${ThemeColors.warning.dark};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
`;

const CompetitorDisplayCard = styled.div`
  background-color: ${ThemeColors.background.paper};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.sm};
`;

const CompetitorDisplayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm};
`;

const CompetitorDisplayName = styled.span`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
`;

const CompetitorStockBadge = styled.span<{ level: number }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${props => {
    if (props.level <= 25) return ThemeColors.error.light;
    if (props.level <= 50) return ThemeColors.warning.light;
    if (props.level <= 75) return ThemeColors.info.light;
    return ThemeColors.success.light;
  }}33;
  color: ${props => {
    if (props.level <= 25) return ThemeColors.error.dark;
    if (props.level <= 50) return ThemeColors.warning.dark;
    if (props.level <= 75) return ThemeColors.info.dark;
    return ThemeColors.success.dark;
  }};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
`;

const CompetitorPhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: ${Spacing.sm};
  margin-top: ${Spacing.sm};
`;

const CompetitorPhotoItem = styled.div`
  position: relative;
  border-radius: ${BorderRadius.sm};
  overflow: hidden;
  aspect-ratio: 1;
  cursor: pointer;
  border: 1px solid ${ThemeColors.border.light};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const VisitTypeBadge = styled.span<{ visitType: VisitType }>`
  display: inline-block;
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${props => {
    switch (props.visitType) {
      case 'setup': return ThemeColors.info.light;
      case 'maintenance': return ThemeColors.success.light;
      case 'requested': return ThemeColors.warning.light;
      case 'other': return ThemeColors.primary.light;
      default: return ThemeColors.gray[100];
    }
  }};
  color: ${props => {
    switch (props.visitType) {
      case 'setup': return ThemeColors.info.dark;
      case 'maintenance': return ThemeColors.success.dark;
      case 'requested': return ThemeColors.warning.dark;
      case 'other': return ThemeColors.primary.dark;
      default: return ThemeColors.text.secondary;
    }
  }};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const ManagerContactBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.secondary.light}33;
  color: ${ThemeColors.secondary.dark};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  margin-bottom: ${Spacing.md};
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
`;

const MediaItem = styled.div`
  position: relative;
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  aspect-ratio: 1;
  cursor: pointer;
  border: 1px solid ${ThemeColors.border.light};
  
  &:hover {
    box-shadow: ${Shadows.md};
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
  gap: ${Spacing.sm};
`;

const DocumentName = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  flex: 1;
`;

const DocumentActions = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const ReplyButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.primary.light}33;
  color: ${ThemeColors.primary.main};
  border: 1px solid ${ThemeColors.primary.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${Spacing.md};
  
  @media (max-width: 768px) {
    border-radius: ${BorderRadius.sm};
    padding: ${Spacing.xs};
    font-size: ${Typography.fontSize.xs};
    margin-top: ${Spacing.sm};
  }
  
  &:hover {
    background-color: ${ThemeColors.primary.light}66;
  }
`;

const RepliesSection = styled.div`
  margin-top: ${Spacing.md};
  padding-top: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const RepliesTitle = styled.h4`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.md};
`;

const ReplyCard = styled.div`
  background-color: ${ThemeColors.gray[50]};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.sm};
  border-left: 3px solid ${ThemeColors.primary.main};
  
  @media (max-width: 768px) {
    border-radius: ${BorderRadius.sm};
    padding: ${Spacing.sm};
    margin-bottom: ${Spacing.xs};
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.xs};
`;

const ReplyAuthor = styled.div`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const ReplyDate = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const ReplyText = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  line-height: ${Typography.lineHeight.relaxed};
  margin: 0;
  white-space: pre-wrap;
`;

const ReplyMediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: ${Spacing.xs};
  margin-top: ${Spacing.sm};
`;

const ReplyDocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacing.xs};
  background-color: white;
  border-radius: ${BorderRadius.sm};
  border: 1px solid ${ThemeColors.border.light};
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: ${Spacing.xs};
  border-radius: ${BorderRadius.round};
  color: ${ThemeColors.text.secondary};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.gray[200]};
    color: ${ThemeColors.text.primary};
  }
`;

const DeleteButton = styled.button`
  background-color: ${ThemeColors.error.light}22;
  border: 1px solid ${ThemeColors.error.light};
  cursor: pointer;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  color: ${ThemeColors.error.main};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  
  &:hover {
    background-color: ${ThemeColors.error.light}44;
    color: ${ThemeColors.error.dark};
    border-color: ${ThemeColors.error.main};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

export const NotesList: React.FC<NotesListProps> = ({ 
  notes, 
  onViewMediaFullScreen,
  onSubmitReply,
  onDeleteNote,
  onDeleteReply,
  onRefreshNotes,
  isManager = false,
  currentUserEmail
}) => {
  const { t } = useTranslation();
  const [isRefreshingNotes, setIsRefreshingNotes] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);
  const [lightboxCaption, setLightboxCaption] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [replyingToNoteId, setReplyingToNoteId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: 'note' | 'reply'; noteId: string; replyId?: string } | null>(null);
  const [userNameCache, setUserNameCache] = useState<Map<string, string>>(new Map());
  
  // Check if current user is one of the specific managers allowed to delete notes and replies
  // Use case-insensitive comparison to handle email variations
  const normalizedEmail = currentUserEmail?.toLowerCase();
  const canDeleteNotes = normalizedEmail === 'devbob.co@gmail.com' || 
                         normalizedEmail === 'chrisg@mckenzieseeds.com';
  const canDeleteReplies = isManager || canDeleteNotes;
  
  // Debug logging
  React.useEffect(() => {
    console.log('NotesList - isManager:', isManager, 'onDeleteNote:', !!onDeleteNote, 'canDeleteNotes:', canDeleteNotes, 'canDeleteReplies:', canDeleteReplies);
  }, [isManager, onDeleteNote, canDeleteNotes, canDeleteReplies]);
  
  const openLightbox = (imageUrl: string, allImages: string[], currentIndex: number, caption?: string) => {
    setLightboxImage(imageUrl);
    setLightboxImages(allImages);
    setLightboxCurrentIndex(currentIndex);
    setLightboxCaption(caption || '');
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => setLightboxOpen(false);

  // Function to get user display name from email
  const getUserDisplayName = async (email: string): Promise<string> => {
    if (!email) return 'Unknown';
    
    // Check cache first
    if (userNameCache.has(email)) {
      return userNameCache.get(email)!;
    }
    
    try {
      const usersRef = collection(db, 'users');
      const emailQuery = query(usersRef, where('email', '==', email));
      const snapshot = await getDocs(emailQuery);
      
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        const firstName = userData.firstName || '';
        const lastName = userData.lastName || '';
        const displayName = userData.displayName || 
                           (firstName && lastName ? `${firstName} ${lastName}`.trim() : firstName) ||
                           userData.username || 
                           email.split('@')[0];
        
        // Cache the result
        setUserNameCache(prev => new Map(prev).set(email, displayName));
        return displayName;
      }
      
      // If not found, try alternative email field
      const altEmailQuery = query(usersRef, where('User_mail', '==', email));
      const altSnapshot = await getDocs(altEmailQuery);
      
      if (!altSnapshot.empty) {
        const userData = altSnapshot.docs[0].data();
        const firstName = userData.firstName || '';
        const lastName = userData.lastName || '';
        const displayName = userData.displayName || 
                           (firstName && lastName ? `${firstName} ${lastName}`.trim() : firstName) ||
                           userData.username || 
                           email.split('@')[0];
        
        setUserNameCache(prev => new Map(prev).set(email, displayName));
        return displayName;
      }
      
      // Fallback to email username part
      const fallbackName = email.split('@')[0];
      setUserNameCache(prev => new Map(prev).set(email, fallbackName));
      return fallbackName;
    } catch (error) {
      console.error('Error fetching user name:', error);
      const fallbackName = email.split('@')[0];
      setUserNameCache(prev => new Map(prev).set(email, fallbackName));
      return fallbackName;
    }
  };

  // Load user names for all notes and replies
  useEffect(() => {
    const loadUserNames = async () => {
      const emailsToLoad = new Set<string>();
      
      notes.forEach(note => {
        if (note.author) emailsToLoad.add(note.author);
        if (note.salesRepName) emailsToLoad.add(note.salesRepName);
        if (note.replies) {
          note.replies.forEach(reply => {
            if (reply.author) emailsToLoad.add(reply.author);
            if (reply.salesRepName) emailsToLoad.add(reply.salesRepName);
          });
        }
      });
      
      // Load names for all unique emails
      for (const email of emailsToLoad) {
        if (!userNameCache.has(email)) {
          await getUserDisplayName(email);
        }
      }
    };
    
    if (notes.length > 0) {
      loadUserNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  // Sort notes based on selected option
  const sortedNotes = useMemo(() => {
    const notesCopy = [...notes];
    
    switch (sortOption) {
      case 'newest':
        return notesCopy.sort((a, b) => {
          const dateA = new Date((a.date || '') + (a.time ? ` ${a.time}` : ''));
          const dateB = new Date((b.date || '') + (b.time ? ` ${b.time}` : ''));
          return dateB.getTime() - dateA.getTime();
        });
      case 'oldest':
        return notesCopy.sort((a, b) => {
          const dateA = new Date((a.date || '') + (a.time ? ` ${a.time}` : ''));
          const dateB = new Date((b.date || '') + (b.time ? ` ${b.time}` : ''));
          return dateA.getTime() - dateB.getTime();
        });
      case 'author':
        return notesCopy.sort((a, b) => {
          const authorA = (a.salesRepName || a.author || '').toLowerCase();
          const authorB = (b.salesRepName || b.author || '').toLowerCase();
          return authorA.localeCompare(authorB);
        });
      case 'author-desc':
        return notesCopy.sort((a, b) => {
          const authorA = (a.salesRepName || a.author || '').toLowerCase();
          const authorB = (b.salesRepName || b.author || '').toLowerCase();
          return authorB.localeCompare(authorA);
        });
      default:
        return notesCopy;
    }
  }, [notes, sortOption]);

  const getSortIcon = () => {
    switch (sortOption) {
      case 'newest':
        return <ArrowDown size={16} />;
      case 'oldest':
        return <ArrowUp size={16} />;
      case 'author':
        return <ArrowUp size={16} />;
      case 'author-desc':
        return <ArrowDown size={16} />;
      default:
        return <ArrowUpDown size={16} />;
    }
  };

  const handleRefreshNotes = async () => {
    if (!onRefreshNotes) return;
    setIsRefreshingNotes(true);
    try {
      await onRefreshNotes();
    } finally {
      setIsRefreshingNotes(false);
    }
  };

  return (
    <>
      <Container>
        <HeaderSection>
          <SectionTitle>{t('storeDetails.previousVisits', 'Previous Visits')}</SectionTitle>
          
          <SortContainer>
            {onRefreshNotes && (
              <RefreshNotesButton
                onClick={handleRefreshNotes}
                disabled={isRefreshingNotes}
                spinning={isRefreshingNotes}
                title={t('storeDetails.refreshNotes', 'Refresh notes')}
              >
                <RefreshCw size={15} />
                {isRefreshingNotes
                  ? t('storeDetails.refreshing', 'Refreshing...')
                  : t('storeDetails.refresh', 'Refresh')}
              </RefreshNotesButton>
            )}
            <SortLabel htmlFor="sort-select">{t('storeDetails.sortBy', 'Sort by:')}</SortLabel>
            <SortSelect
              id="sort-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
            >
              <option value="newest">{t('storeDetails.newestFirst', 'Newest First')}</option>
              <option value="oldest">{t('storeDetails.oldestFirst', 'Oldest First')}</option>
              <option value="author">{t('storeDetails.authorAZ', 'Author A-Z')}</option>
              <option value="author-desc">{t('storeDetails.authorZA', 'Author Z-A')}</option>
            </SortSelect>
            <SortIcon sortOption={sortOption}>
              {getSortIcon()}
            </SortIcon>
          </SortContainer>
        </HeaderSection>

        {onRefreshNotes && (
          <RefreshHintBanner>
            <RefreshCw size={13} />
            {t('storeDetails.noteSeeingNote', 'Not seeing your note? Tap Refresh to load the latest.')}
          </RefreshHintBanner>
        )}
        
        {sortedNotes.length === 0 ? (
          <EmptyState>
            <FileText size={48} color={ThemeColors.text.hint} />
            <EmptyStateText>No notes have been added yet.</EmptyStateText>
          </EmptyState>
        ) : (
          sortedNotes.map(note => (
            <NoteCard key={note.id}>
              <NoteHeader>
                <AuthorInfo>
                  <Author>
                    <User size={16} />
                    {userNameCache.get(note.salesRepName || note.author) || note.salesRepName || note.author}
                  </Author>
                </AuthorInfo>
                <TimeInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.sm }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <DateText>
                        <Calendar size={14} />
                        {note.date}
                      </DateText>
                      {note.time && (
                        <TimeText>
                          <Clock size={12} />
                          {note.time}
                        </TimeText>
                      )}
                    </div>
                    {onDeleteNote && canDeleteNotes && (
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmation({ type: 'note', noteId: note.id });
                        }}
                        title="Delete note"
                        aria-label="Delete note"
                      >
                        <Trash2 size={18} />
                      </DeleteButton>
                    )}
                  </div>
                </TimeInfo>
              </NoteHeader>
              
              <NoteBody>
                <NoteText>{note.text}</NoteText>
                
                {note.nextVisitDate && (
                  <NextVisitBadge>
                    <Calendar size={14} />
                    Next Visit: {note.nextVisitDate}
                  </NextVisitBadge>
                )}
                
                {note.storeLevel !== undefined && (
                  <Section>
                    <SectionHeader>McKenzie Seeds Package Level:</SectionHeader>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: Spacing.md,
                      marginBottom: Spacing.md 
                    }}>
                      <div style={{ 
                        flex: 1, 
                        height: '8px', 
                        background: ThemeColors.gray[200], 
                        borderRadius: BorderRadius.md,
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${note.storeLevel}%`,
                          height: '100%',
                          background: `linear-gradient(90deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark})`,
                          borderRadius: BorderRadius.md,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <span style={{
                        fontSize: Typography.fontSize.sm,
                        fontWeight: Typography.fontWeight.semiBold,
                        color: ThemeColors.primary.main,
                        minWidth: '45px'
                      }}>
                        {note.storeLevel}%
                      </span>
                    </div>
                  </Section>
                )}
                
                {note.lowStockCategories && note.lowStockCategories.length > 0 && (
                  <Section>
                    <SectionHeader>
                      <Package size={14} />
                      Low Stock Category:
                    </SectionHeader>
                    <CompetitorList>
                      {note.lowStockCategories.map((category, index) => (
                        <LowStockBadge key={index}>
                          {category}
                        </LowStockBadge>
                      ))}
                    </CompetitorList>
                  </Section>
                )}
                
                {note.visitType && note.visitType !== '' && (
                  <Section>
                    <SectionHeader>{t('notes.visitType', 'Visit Type')}:</SectionHeader>
                    <VisitTypeBadge visitType={note.visitType}>
                      {note.visitType === 'setup' && t('notes.visitTypeSetup', 'Setup Visit')}
                      {note.visitType === 'maintenance' && t('notes.visitTypeMaintenance', 'Maintenance Visit')}
                      {note.visitType === 'requested' && t('notes.visitTypeRequested', 'Store/Manager Requested Visit')}
                      {note.visitType === 'other' && (note.visitReason || t('notes.visitTypeOther', 'Other'))}
                    </VisitTypeBadge>
                    {note.visitType === 'other' && note.visitReason && (
                      <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                        {note.visitReason}
                      </div>
                    )}
                  </Section>
                )}

                {note.requestManagerContact && (
                  <ManagerContactBadge>
                    <AlertCircle size={14} />
                    Request Manager Contact
                  </ManagerContactBadge>
                )}
                
                {note.competitors && note.competitors.length > 0 && (
                  <Section>
                    <SectionHeader>Competitors Spotted:</SectionHeader>
                    <CompetitorList>
                      {note.competitors.map((competitor, index) => (
                        <CompetitorBadge key={index}>
                          {competitor}
                        </CompetitorBadge>
                      ))}
                    </CompetitorList>
                  </Section>
                )}
                
                {/* Competitor Display Data with Photos and Stock Levels */}
                {note.competitorData && note.competitorData.length > 0 && (
                  <Section>
                    <SectionHeader>Competitor Display Details:</SectionHeader>
                    {note.competitorData.map((competitor, cIndex) => {
                      // Debug logging
                      if (process.env.NODE_ENV === 'development') {
                        console.log(`[NotesList] Rendering competitor ${cIndex}:`, {
                          name: competitor.name,
                          photoUrls: competitor.photoUrls,
                          photoUrlsLength: competitor.photoUrls?.length,
                          stockLevel: competitor.stockLevel
                        });
                      }
                      
                      // Ensure photoUrls is an array
                      const photoUrls = Array.isArray(competitor.photoUrls) ? competitor.photoUrls : [];
                      
                      return (
                        <CompetitorDisplayCard key={`competitor-data-${cIndex}`}>
                          <CompetitorDisplayHeader>
                            <CompetitorDisplayName>{competitor.name}</CompetitorDisplayName>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <CompetitorStockBadge level={competitor.stockLevel}>
                                Stock: {competitor.stockLevel}%
                              </CompetitorStockBadge>
                              {competitor.displayPanels !== undefined && competitor.displayPanels > 0 && (
                                <CompetitorStockBadge level={75}>
                                  Panels: {competitor.displayPanels}
                                </CompetitorStockBadge>
                              )}
                            </div>
                          </CompetitorDisplayHeader>
                          {photoUrls.length > 0 && (
                            <CompetitorPhotoGrid>
                              {photoUrls.map((photoUrl, pIndex) => {
                                if (process.env.NODE_ENV === 'development') {
                                  console.log(`[NotesList] Rendering photo ${pIndex} for ${competitor.name}:`, photoUrl);
                                }
                                return (
                                  <CompetitorPhotoItem 
                                    key={`competitor-photo-${cIndex}-${pIndex}`}
                                    onClick={() => {
                                      openLightbox(photoUrl, photoUrls, pIndex, `${competitor.name} Display ${pIndex + 1}`);
                                    }}
                                  >
                                    <img 
                                      src={photoUrl} 
                                      alt={`${competitor.name} display ${pIndex + 1}`}
                                      onError={(e) => {
                                        console.error(`[NotesList] Failed to load image for ${competitor.name}:`, photoUrl);
                                        console.error('Error:', e);
                                      }}
                                      onLoad={() => {
                                        if (process.env.NODE_ENV === 'development') {
                                          console.log(`[NotesList] Successfully loaded image for ${competitor.name}:`, photoUrl);
                                        }
                                      }}
                                    />
                                  </CompetitorPhotoItem>
                                );
                              })}
                            </CompetitorPhotoGrid>
                          )}
                        </CompetitorDisplayCard>
                      );
                    })}
                  </Section>
                )}
                
                {note.media && note.media.length > 0 && (
                  <Section>
                    <SectionHeader>Media:</SectionHeader>
                    <MediaGrid>
                      {note.media.map((media, index) => {
                        const imageUrl = media.dataUrl || media.url;
                        const allImageUrls = note.media
                          .map(m => m.dataUrl || m.url)
                          .filter(url => url) as string[];
                        
                        return (
                        <MediaItem 
                          key={`media-${index}`}
                          onClick={() => {
                            if (imageUrl) {
                                openLightbox(imageUrl, allImageUrls, index, media.filename);
                            }
                          }}
                        >
                          <MediaImage 
                              src={imageUrl} 
                            alt={`Media ${index}`}
                          />
                        </MediaItem>
                        );
                      })}
                    </MediaGrid>
                  </Section>
                )}
                
                {note.documents && note.documents.length > 0 && (
                  <Section>
                    <SectionHeader>Documents:</SectionHeader>
                    <DocumentsList>
                      {note.documents.map((doc, index) => (
                        <DocumentItem key={`doc-${index}`}>
                          <FileText size={18} />
                          <DocumentName>
                            {doc.filename || `Document ${index + 1}`}
                          </DocumentName>
                          <DocumentActions>
                            <ActionButton 
                              onClick={() => onViewMediaFullScreen && onViewMediaFullScreen(doc)}
                              title="View"
                            >
                              <ExternalLink size={16} />
                            </ActionButton>
                            <ActionButton 
                              title="Download"
                              onClick={() => {
                                if (doc.url) {
                                  window.open(doc.url, '_blank');
                                }
                              }}
                            >
                              <Download size={16} />
                            </ActionButton>
                          </DocumentActions>
                        </DocumentItem>
                      ))}
                    </DocumentsList>
                  </Section>
                )}

                {onSubmitReply && (
                  <ReplyButton onClick={() => setReplyingToNoteId(note.id)}>
                    <MessageSquare size={14} />
                    {t('notes.reply', 'Reply')}
                  </ReplyButton>
                )}

                {replyingToNoteId === note.id && onSubmitReply && (
                  <ReplyForm
                    onSubmitReply={async (text, media, documents, compressionEnabled) => {
                      await onSubmitReply(note.id, text, media, documents, compressionEnabled);
                      setReplyingToNoteId(null);
                    }}
                    onCancel={() => setReplyingToNoteId(null)}
                    authorName={note.salesRepName || note.author}
                    storeId={''}
                  />
                )}

                {note.replies && note.replies.length > 0 && (
                  <RepliesSection>
                    <RepliesTitle>
                      {t('notes.replies', 'Replies')} ({note.replies.length})
                    </RepliesTitle>
                    {note.replies.map((reply, index) => (
                      <ReplyCard key={reply.id || `reply-${index}`}>
                        <ReplyHeader>
                          <ReplyAuthor>
                            <User size={12} />
                            {userNameCache.get(reply.salesRepName || reply.author) || reply.salesRepName || reply.author}
                          </ReplyAuthor>
                          <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.sm }}>
                          <ReplyDate>
                            <Calendar size={10} />
                            {reply.date} {reply.time && `• ${reply.time}`}
                          </ReplyDate>
                            {canDeleteReplies && onDeleteReply && (
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmation({ type: 'reply', noteId: note.id, replyId: reply.id });
                                }}
                                title="Delete reply"
                                aria-label="Delete reply"
                                style={{ padding: '4px', marginLeft: Spacing.xs }}
                              >
                                <Trash2 size={14} />
                              </DeleteButton>
                            )}
                          </div>
                        </ReplyHeader>
                        <ReplyText>{reply.text}</ReplyText>
                        {reply.media && reply.media.length > 0 && (
                          <ReplyMediaGrid>
                            {reply.media.map((media, mediaIndex) => {
                              const imageUrl = media.dataUrl || media.url;
                              const allImageUrls = reply.media
                                .map(m => m.dataUrl || m.url)
                                .filter(url => url) as string[];
                              
                              return (
                              <MediaItem 
                                key={`reply-media-${mediaIndex}`}
                                onClick={() => {
                                  if (imageUrl) {
                                      openLightbox(imageUrl, allImageUrls, mediaIndex, media.filename);
                                  }
                                }}
                              >
                                <MediaImage 
                                    src={imageUrl} 
                                  alt={`Reply media ${mediaIndex}`}
                                />
                              </MediaItem>
                              );
                            })}
                          </ReplyMediaGrid>
                        )}
                        {reply.documents && reply.documents.length > 0 && (
                          <div style={{ marginTop: Spacing.xs }}>
                            {reply.documents.map((doc, docIndex) => (
                              <ReplyDocumentItem key={`reply-doc-${docIndex}`}>
                                <FileText size={12} />
                                <span>{doc.filename || `Document ${docIndex + 1}`}</span>
                                {doc.url && (
                                  <ActionButton 
                                    onClick={() => window.open(doc.url, '_blank')}
                                    title="Download"
                                  >
                                    <Download size={12} />
                                  </ActionButton>
                                )}
                              </ReplyDocumentItem>
                            ))}
                          </div>
                        )}
                      </ReplyCard>
                    ))}
                  </RepliesSection>
                )}
              </NoteBody>
            </NoteCard>
          ))
        )}
      </Container>
      
      <Lightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage}
        images={lightboxImages}
        currentIndex={lightboxCurrentIndex}
        onClose={closeLightbox}
        caption={lightboxCaption}
      />
      
      <ConfirmationModal
        isOpen={deleteConfirmation !== null}
        title={deleteConfirmation?.type === 'note' ? 'Delete Note' : 'Delete Reply'}
        message={deleteConfirmation?.type === 'note' 
          ? 'Are you sure you want to delete this note? This action cannot be undone.'
          : 'Are you sure you want to delete this reply? This action cannot be undone.'}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          if (deleteConfirmation) {
            if (deleteConfirmation.type === 'note' && onDeleteNote) {
              onDeleteNote(deleteConfirmation.noteId);
            } else if (deleteConfirmation.type === 'reply' && onDeleteReply && deleteConfirmation.replyId) {
              onDeleteReply(deleteConfirmation.noteId, deleteConfirmation.replyId);
            }
            setDeleteConfirmation(null);
          }
        }}
        onCancel={() => setDeleteConfirmation(null)}
      />
    </>
  );
};